import { Controller } from "@hotwired/stimulus";

/**
 * Story Carousel Controller
 * 
 * Manages Instagram-style story slides with tap navigation, keyboard controls,
 * and progress indicators. Supports optional auto-advance functionality.
 * 
 * @class StoryCarouselController
 * @extends {Controller}
 */
export default class extends Controller {
  static targets = ["slide"];
  static values = { 
    total: Number,
    closePath: { type: String, default: "/" },
    autoAdvance: { type: Boolean, default: false },
    autoAdvanceDelay: { type: Number, default: 5000 }
  };

  /**
   * Initialize carousel on controller connection
   */
  connect() {
    this.currentIndex = 0;
    this.autoAdvanceTimer = null;
    
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener("keydown", this.handleKeydown);
    
    if (this.autoAdvanceValue) {
      this.startAutoAdvance();
    }
  }

  /**
   * Cleanup on controller disconnection
   */
  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
    this.stopAutoAdvance();
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeydown(event) {
    // Only handle if carousel is visible
    if (!this.element.offsetParent) return;
    
    switch(event.key) {
      case "ArrowLeft":
        event.preventDefault();
        this.previousSlide();
        break;
      case "ArrowRight":
        event.preventDefault();
        this.nextSlide();
        break;
      case "Escape":
        event.preventDefault();
        this.closeCarousel();
        break;
    }
  }

  /**
   * Navigate to next slide or close carousel at end
   */
  nextSlide() {
    if (this.currentIndex < this.totalValue - 1) {
      this.goToSlide(this.currentIndex + 1);
    } else {
      this.closeCarousel();
    }
  }

  /**
   * Navigate to previous slide
   */
  previousSlide() {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  /**
   * Navigate to specific slide by index
   * @param {number} index - The slide index to navigate to
   */
  goToSlide(index) {
    if (index < 0 || index >= this.totalValue) return;
    
    // Hide current slide
    if (this.slideTargets[this.currentIndex]) {
      this.slideTargets[this.currentIndex].classList.add("hidden");
      this.slideTargets[this.currentIndex].classList.remove("block");
    }
    
    // Show new slide
    if (this.slideTargets[index]) {
      this.slideTargets[index].classList.remove("hidden");
      this.slideTargets[index].classList.add("block");
    }
    
    this.currentIndex = index;
    this.updateProgressIndicators();
    
    if (this.autoAdvanceValue) {
      this.restartAutoAdvance();
    }
    
    this.dispatch("slideChanged", { detail: { index: this.currentIndex } });
  }

  /**
   * Update progress indicator states based on current slide
   */
  updateProgressIndicators() {
    this.slideTargets.forEach(slide => {
      const indicators = slide.querySelectorAll('[data-progress-indicator]');
      indicators.forEach((indicator, index) => {
        indicator.classList.toggle('bg-white', index <= this.currentIndex);
        indicator.classList.toggle('bg-white/30', index > this.currentIndex);
      });
    });
  }

  /**
   * Close carousel and navigate to specified path
   */
  closeCarousel() {
    if (window.Turbo) {
      window.Turbo.visit(this.closePathValue);
    } else {
      window.location.href = this.closePathValue;
    }
  }

  /**
   * Start auto-advance timer
   */
  startAutoAdvance() {
    if (!this.autoAdvanceValue) return;
    
    this.autoAdvanceTimer = setTimeout(() => {
      this.nextSlide();
    }, this.autoAdvanceDelayValue);
  }

  /**
   * Stop auto-advance timer
   */
  stopAutoAdvance() {
    if (this.autoAdvanceTimer) {
      clearTimeout(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }
  }

  /**
   * Restart auto-advance timer
   */
  restartAutoAdvance() {
    this.stopAutoAdvance();
    this.startAutoAdvance();
  }

  /**
   * Dispatch custom event
   * @param {string} eventName - Name of the event to dispatch
   * @param {Object} options - Event options
   */
  dispatch(eventName, options = {}) {
    this.element.dispatchEvent(new CustomEvent(eventName, options));
  }
}